import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Mail, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../services/api';
import { toast } from 'sonner';

export const EditReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formFields, setFormFields] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    batch: '',
    quantity: '',
    category_id: 'none',
    status_id: '',
    custom_fields: {},
    notification_emails: []
  });
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [reportRes, categoriesRes, statusesRes, fieldsRes] = await Promise.all([
        api.get(`/reports/${id}`),
        api.get('/categories'),
        api.get('/statuses'),
        api.get('/form-fields')
      ]);

      const report = reportRes.data;
      setFormData({
        title: report.title,
        batch: report.batch,
        quantity: report.quantity.toString(),
        category_id: report.category_id || 'none',
        status_id: report.status_id,
        custom_fields: report.custom_fields || {},
        notification_emails: report.notification_emails || []
      });

      setCategories(categoriesRes.data);
      setStatuses(statusesRes.data);
      setFormFields(fieldsRes.data);
    } catch (error) {
      toast.error('Chyba při načítání hlášení');
      navigate('/reports');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.title || !formData.batch || !formData.quantity || !formData.status_id) {
        toast.error('Vyplňte všechna povinná pole');
        setLoading(false);
        return;
      }

      // Check custom required fields
      for (const field of formFields) {
        if (field.required && !formData.custom_fields[field.label]) {
          toast.error(`Pole "${field.label}" je povinné`);
          setLoading(false);
          return;
        }
      }

      await api.put(`/reports/${id}`, {
        title: formData.title,
        batch: formData.batch,
        quantity: parseInt(formData.quantity),
        category_id: formData.category_id && formData.category_id !== 'none' ? formData.category_id : null,
        status_id: formData.status_id,
        custom_fields: formData.custom_fields,
        notification_emails: formData.notification_emails
      });

      toast.success('Hlášení aktualizováno');
      navigate(`/reports/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při aktualizaci hlášení');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomFieldChange = (label, value) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [label]: value
      }
    }));
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Zadejte platnou emailovou adresu');
      return;
    }
    
    if (formData.notification_emails.includes(email)) {
      toast.error('Tento email už je přidán');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      notification_emails: [...prev.notification_emails, email]
    }));
    setEmailInput('');
  };

  const removeEmail = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      notification_emails: prev.notification_emails.filter(e => e !== emailToRemove)
    }));
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-500">Načítání...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8" data-testid="edit-report-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(`/reports/${id}`)}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl border border-cyan-200 p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-slate-900 mb-6">Upravit hlášení</h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Default Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="sm:col-span-2">
                  <Label htmlFor="title">
                    Název <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Přesný název produktu"
                    required
                    data-testid="title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="batch">
                    Šarže <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="batch"
                    value={formData.batch}
                    onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                    placeholder="Číslo šarže"
                    required
                    data-testid="batch-input"
                  />
                </div>

                <div>
                  <Label htmlFor="quantity">
                    Počet kusů <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder="0"
                    required
                    min="1"
                    data-testid="quantity-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Kategorie</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger id="category" data-testid="category-select">
                      <SelectValue placeholder="Vyberte kategorii" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Bez kategorie</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status">
                    Stav <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.status_id}
                    onValueChange={(value) => setFormData({ ...formData, status_id: value })}
                  >
                    <SelectTrigger id="status" data-testid="status-select">
                      <SelectValue placeholder="Vyberte stav" />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(status => (
                        <SelectItem key={status.id} value={status.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: status.color }}
                            />
                            {status.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Custom Fields */}
              {formFields.length > 0 && (
                <div className="border-t border-cyan-200 pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Další informace</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {formFields.map((field) => (
                      <div
                        key={field.id}
                        className={field.width === 'full' ? 'sm:col-span-2' : ''}
                      >
                        <Label htmlFor={`custom-${field.id}`}>
                          {field.label}
                          {field.required && <span className="text-red-500"> *</span>}
                        </Label>
                        {field.field_type === 'textarea' ? (
                          <Textarea
                            id={`custom-${field.id}`}
                            value={formData.custom_fields[field.label] || ''}
                            onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                          />
                        ) : field.field_type === 'select' ? (
                          <Select
                            value={formData.custom_fields[field.label] || ''}
                            onValueChange={(value) => handleCustomFieldChange(field.label, value)}
                          >
                            <SelectTrigger id={`custom-${field.id}`} data-testid={`custom-field-${field.id}`}>
                              <SelectValue placeholder={field.placeholder || 'Vyberte'} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map(option => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : field.field_type === 'date' ? (
                          <DatePicker
                            value={formData.custom_fields[field.label] || ''}
                            onChange={(value) => handleCustomFieldChange(field.label, value)}
                            placeholder={field.placeholder || 'Vyberte datum'}
                            data-testid={`custom-field-${field.id}`}
                          />
                        ) : (
                          <Input
                            id={`custom-${field.id}`}
                            type={field.field_type}
                            value={formData.custom_fields[field.label] || ''}
                            onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Notifications */}
              <div className="border-t border-cyan-200 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="h-5 w-5 text-cyan-600" />
                  <h3 className="text-lg font-semibold text-slate-900">Email notifikace</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Osoby na těchto adresách budou notifikovány při jakékoliv změně tohoto hlášení.
                </p>
                
                <div className="flex gap-2 mb-3">
                  <Input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={handleEmailKeyPress}
                    placeholder="email@example.com"
                    className="flex-1"
                    data-testid="notification-email-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addEmail}
                    className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    data-testid="add-email-button"
                  >
                    Přidat
                  </Button>
                </div>
                
                {formData.notification_emails.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="email-list">
                    {formData.notification_emails.map((email) => (
                      <Badge
                        key={email}
                        variant="secondary"
                        className="bg-cyan-50 text-cyan-700 hover:bg-cyan-100 pl-3 pr-1 py-1.5 flex items-center gap-1"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEmail(email)}
                          className="ml-1 hover:bg-cyan-200 rounded-full p-0.5"
                          data-testid={`remove-email-${email}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/reports/${id}`)}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Zrušit
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600"
                  data-testid="submit-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Ukládání...' : 'Uložit změny'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
